import { useState } from 'react';
import {
  Badge,
  Button,
  Center,
  Group,
  Loader,
  Menu,
  Modal,
  Paper,
  Select,
  Stack,
  Table,
  Text,
  ThemeIcon,
  TextInput,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconChevronDown, IconPlus, IconUsers } from '@tabler/icons-react';
import { useSubscription } from 'urql';
import { ErrorAlert } from '@/components/error-alert';
import { api } from '@/lib/api';
import { getErrorMessage } from '@/lib/error-message';
import { SystemOrganizationMembersSubscription } from './graphql';
import type { SystemOrganizationMember } from './graphql';

const memberRoles = ['owner', 'admin', 'member'] as const;

type MemberRole = (typeof memberRoles)[number];

const roleColors: Record<string, string> = {
  owner: 'indigo',
  admin: 'cyan',
  member: 'gray',
};

const statusColors: Record<string, string> = {
  active: 'var(--mantine-color-green-6)',
  invited: 'var(--mantine-color-yellow-6)',
  disabled: 'var(--mantine-color-red-6)',
};

const roleOptions = memberRoles.map((r) => ({ value: r, label: r.charAt(0).toUpperCase() + r.slice(1) }));

function isMemberRole(value: string | null): value is MemberRole {
  return memberRoles.some((r) => r === value);
}

function StatusDot({ status }: { status: string }) {
  return (
    <Group gap="xs">
      <div
        style={{
          width: 8,
          height: 8,
          borderRadius: '50%',
          backgroundColor: statusColors[status] ?? 'var(--mantine-color-gray-5)',
        }}
      />
      <Text size="sm" c="dimmed" tt="capitalize">
        {status}
      </Text>
    </Group>
  );
}

function MemberRow({
  member,
  organizationId,
  onError,
}: {
  member: SystemOrganizationMember;
  organizationId: string;
  onError: (message: string) => void;
}) {
  const [isBusy, setIsBusy] = useState(false);

  const changeRole = async (role: MemberRole) => {
    setIsBusy(true);

    const { error } = await api.organizations({ organizationId }).members({ memberId: member.id }).patch({ role });

    setIsBusy(false);

    if (error) onError(getErrorMessage(error, 'Failed to change the member role'));
  };

  const setDisabled = async (disabled: boolean) => {
    setIsBusy(true);

    const endpoint = api.organizations({ organizationId }).members({ memberId: member.id });
    const { error } = disabled ? await endpoint.disable.post() : await endpoint.enable.post();

    setIsBusy(false);

    if (error)
      onError(getErrorMessage(error, disabled ? 'Failed to disable the member' : 'Failed to enable the member'));
  };

  return (
    <Table.Tr>
      <Table.Td>
        <Text size="sm" fw={500}>
          {member.email}
        </Text>
      </Table.Td>
      <Table.Td>
        <Menu disabled={isBusy}>
          <Menu.Target>
            <Badge
              variant="light"
              color={roleColors[member.role] ?? 'gray'}
              style={{ cursor: 'pointer' }}
              rightSection={<IconChevronDown size={10} />}
            >
              {member.role}
            </Badge>
          </Menu.Target>
          <Menu.Dropdown>
            {memberRoles.map((role) => (
              <Menu.Item key={role} onClick={() => void changeRole(role)}>
                {role.charAt(0).toUpperCase() + role.slice(1)}
              </Menu.Item>
            ))}
          </Menu.Dropdown>
        </Menu>
      </Table.Td>
      <Table.Td>
        <StatusDot status={member.status} />
      </Table.Td>
      <Table.Td>
        <Text size="sm" c="dimmed">
          {member.joinedAt ? new Date(member.joinedAt).toLocaleDateString() : '—'}
        </Text>
      </Table.Td>
      <Table.Td>
        {member.status === 'disabled' ? (
          <Button variant="subtle" size="compact-sm" loading={isBusy} onClick={() => void setDisabled(false)}>
            Enable
          </Button>
        ) : (
          <Button
            variant="subtle"
            color="red"
            size="compact-sm"
            loading={isBusy}
            onClick={() => void setDisabled(true)}
          >
            Disable
          </Button>
        )}
      </Table.Td>
    </Table.Tr>
  );
}

function AddMemberModal({
  organizationId,
  opened,
  onClose,
}: {
  organizationId: string;
  opened: boolean;
  onClose: () => void;
}) {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<string | null>('member');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const close = () => {
    setEmail('');
    setRole('member');
    setError(null);
    onClose();
  };

  const submit = async () => {
    if (!isMemberRole(role)) return;

    setError(null);
    setIsSubmitting(true);

    const { data, error: apiError } = await api
      .organizations({ organizationId })
      .members.post({ email: email.trim(), role });

    setIsSubmitting(false);

    if (apiError) {
      setError(getErrorMessage(apiError, 'Failed to add the member'));

      return;
    }

    if (data && data.status === 'invited' && !data.emailSent) {
      notifications.show({
        color: 'yellow',
        title: 'Invitation email not sent',
        message: `${email.trim()} was invited but the invitation email could not be delivered.`,
      });
    }

    close();
  };

  return (
    <Modal opened={opened} onClose={close} title="Add Member">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        <Stack gap="md">
          <ErrorAlert message={error} />
          <TextInput
            label="Email"
            placeholder="user@example.com"
            value={email}
            onChange={(e) => setEmail(e.currentTarget.value)}
            data-autofocus
          />
          <Select label="Role" data={roleOptions} value={role} onChange={setRole} />
          <Group justify="flex-end" mt="xs">
            <Button variant="subtle" type="button" onClick={close}>
              Cancel
            </Button>
            <Button type="submit" loading={isSubmitting} disabled={!email.trim() || !isMemberRole(role)}>
              Add Member
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}

function EmptyMembers({ onAdd }: { onAdd: () => void }) {
  return (
    <Paper p="xl" withBorder radius="md">
      <Stack align="center" gap="md">
        <ThemeIcon size={48} radius="xl" variant="light" color="gray">
          <IconUsers size={24} />
        </ThemeIcon>
        <Stack gap={2} align="center">
          <Text fw={500}>No members</Text>
          <Text size="sm" c="dimmed">
            Invite people to this organization.
          </Text>
        </Stack>
        <Button variant="light" leftSection={<IconPlus size={16} />} onClick={onAdd}>
          Add Member
        </Button>
      </Stack>
    </Paper>
  );
}

export function MembersPanel({ organizationId }: { organizationId: string }) {
  const [addOpened, setAddOpened] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [{ data, error: queryError }] = useSubscription({
    query: SystemOrganizationMembersSubscription,
    variables: { organizationId },
  });

  if (queryError) return <ErrorAlert message={getErrorMessage(queryError, 'Unable to load members')} />;

  if (!data) {
    return (
      <Center h="30vh">
        <Loader size="lg" />
      </Center>
    );
  }

  return (
    <>
      <Stack gap="lg">
        <Group justify="space-between">
          <Text size="sm" c="dimmed">
            Manage who has access to this organization and their roles.
          </Text>
          <Button leftSection={<IconPlus size={16} />} onClick={() => setAddOpened(true)}>
            Add Member
          </Button>
        </Group>

        <ErrorAlert message={error} />

        {data.organizationMembers.length === 0 ? (
          <EmptyMembers onAdd={() => setAddOpened(true)} />
        ) : (
          <Paper p={0} withBorder radius="md" style={{ overflow: 'hidden' }}>
            <Table>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Email</Table.Th>
                  <Table.Th>Role</Table.Th>
                  <Table.Th>Status</Table.Th>
                  <Table.Th>Joined</Table.Th>
                  <Table.Th w={110} />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {data.organizationMembers.map((member) => (
                  <MemberRow key={member.id} member={member} organizationId={organizationId} onError={setError} />
                ))}
              </Table.Tbody>
            </Table>
          </Paper>
        )}
      </Stack>

      <AddMemberModal organizationId={organizationId} opened={addOpened} onClose={() => setAddOpened(false)} />
    </>
  );
}
